// Which products a partner may read.
//
// The screen is category-first because that is how the decision is made — "open
// leather goods to them" — while the storage is per product, because that is
// what the read path filters on and what stays honest when a product changes
// category.
//
// A category is a bulk action, NOT a standing rule. Opening one shares what is
// in it today; a product added tomorrow stays closed. Nothing keeps itself up to
// date, so the "not shared" count does the reminding — that number going from 0
// to 3 is the only thing that will tell an operator the catalogue grew.

import { useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { PackageOpen, Lock, Check } from 'lucide-react'

import { toast } from '../../lib/toast'
import { API_BASE } from '../../lib/api'
import { useT } from '../../i18n'
import { partnerApiI18n } from '../../i18n/admin/partner-api'
import { DataTableShell } from '../../components/ui/data-table-shell'

type PartnerOrg = { id: string; name: string; org_type: string }

type CategoryCoverage = {
  category: string
  total: number
  shared: number
  unshared: number
}

type ProductCoverage = {
  product_id: string
  display_code: string | null
  title: string
  category: string
  shared: boolean
}

type BulkResult = {
  shared: number
  unshared: number
  unchanged: number
  skipped_not_sellable: number
}

export function PartnerCatalogAccess() {
  const t = useT(partnerApiI18n).catalog
  const queryClient = useQueryClient()
  const [orgId, setOrgId] = useState('')
  const [openCategory, setOpenCategory] = useState<string | null>(null)

  const orgsQuery = useQuery({
    queryKey: ['admin-partner-orgs'],
    queryFn: async () => {
      const res = await fetch(`${API_BASE}/orgs?org_type=BO3&limit=200`)
      if (!res.ok) throw new Error(`${res.status}`)
      return ((await res.json()) as { items: PartnerOrg[] }).items ?? []
    },
  })

  const coverageQuery = useQuery({
    queryKey: ['admin-partner-coverage', orgId],
    enabled: orgId !== '',
    queryFn: async () => {
      const res = await fetch(
        `${API_BASE}/admin/partner-catalog/coverage?org_id=${orgId}`,
      )
      if (!res.ok) throw new Error(`${res.status}`)
      return (await res.json()) as { categories: CategoryCoverage[] }
    },
  })

  const productsQuery = useQuery({
    queryKey: ['admin-partner-coverage-products', orgId, openCategory],
    enabled: orgId !== '' && openCategory !== null,
    queryFn: async () => {
      const res = await fetch(
        `${API_BASE}/admin/partner-catalog/products?org_id=${orgId}&category=${encodeURIComponent(
          openCategory ?? '',
        )}`,
      )
      if (!res.ok) throw new Error(`${res.status}`)
      return (await res.json()) as { products: ProductCoverage[] }
    },
  })

  const bulk = useMutation({
    mutationFn: async (vars: { productIds: string[]; shared: boolean }) => {
      const res = await fetch(`${API_BASE}/admin/partner-catalog/coverage`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          org_id: orgId,
          product_ids: vars.productIds,
          shared: vars.shared,
        }),
      })
      if (!res.ok) throw new Error(`${res.status}`)
      return (await res.json()) as BulkResult
    },
    onSuccess: (result) => {
      // Say what happened, not "saved" — the counts are the whole point.
      const parts: string[] = []
      if (result.shared) parts.push(t.opened(result.shared))
      if (result.unshared) parts.push(t.closed(result.unshared))
      if (result.unchanged) parts.push(t.unchanged(result.unchanged))
      if (result.skipped_not_sellable) {
        parts.push(t.skipped(result.skipped_not_sellable))
      }
      toast.success(parts.join(' · ') || t.nothingToChange)
      void queryClient.invalidateQueries({ queryKey: ['admin-partner-coverage', orgId] })
      void queryClient.invalidateQueries({
        queryKey: ['admin-partner-coverage-products', orgId, openCategory],
      })
    },
    onError: () => toast.error(t.applyFailed),
  })

  const categories = useMemo(
    () => coverageQuery.data?.categories ?? [],
    [coverageQuery.data],
  )
  const products = useMemo(
    () => productsQuery.data?.products ?? [],
    [productsQuery.data],
  )

  return (
    <div className="space-y-5">
      <header className="space-y-1">
        <h2 className="flex items-center gap-2 text-lg font-semibold text-slate-900">
          <PackageOpen className="h-4 w-4" />
          {t.title}
        </h2>
        <p className="text-sm text-slate-500">
          {t.descriptionBefore}<strong>{t.descriptionToday}</strong>{t.descriptionAfter}
        </p>
      </header>

      <label className="flex flex-col gap-1">
        <span className="text-[11px] uppercase tracking-wide text-slate-500">{t.partner}</span>
        <select
          value={orgId}
          onChange={(e) => {
            setOrgId(e.target.value)
            setOpenCategory(null)
          }}
          className="min-w-[260px] max-w-sm rounded border border-slate-300 px-2 py-1.5 text-sm"
        >
          <option value="">{t.choosePartner}</option>
          {orgsQuery.data?.map((org) => (
            <option key={org.id} value={org.id}>
              {org.name}
            </option>
          ))}
        </select>
      </label>

      {orgId === '' ? null : (
        <DataTableShell>
          <table>
            <thead>
              <tr>
                <th className="py-2">{t.columns.category}</th>
                <th className="py-2 text-right">{t.columns.shared}</th>
                <th className="py-2 text-right">{t.columns.notShared}</th>
                <th className="py-2" />
              </tr>
            </thead>
            <tbody>
              {categories.length === 0 && (
                <tr>
                  <td colSpan={4} className="py-6 text-center text-[var(--color-text-muted)]">
                    {coverageQuery.isPending ? t.loading : t.noProducts}
                  </td>
                </tr>
              )}
              {categories.map((row) => {
                const isOpen = openCategory === row.category
                return (
                  <>
                    <tr key={row.category}>
                      <td className="py-2 text-[var(--color-text)]">{row.category}</td>
                      <td className="py-2 text-right tabular-nums text-[var(--color-text)]">
                        {row.shared} / {row.total}
                      </td>
                      <td
                        className={`py-2 text-right tabular-nums ${
                          row.unshared > 0 && row.shared > 0
                            ? 'font-medium text-[var(--color-warning)]'
                            : 'text-[var(--color-text-muted)]'
                        }`}
                        title={
                          row.unshared > 0 && row.shared > 0
                            ? t.grownTitle
                            : undefined
                        }
                      >
                        {row.unshared}
                      </td>
                      <td className="py-2 text-right">
                        {/* Expand, then act. There is no shortcut that opens a
                            category without showing what is in it: the operator
                            should approve a list they have seen, and the list
                            can change between rendering and clicking. */}
                        <button
                          type="button"
                          onClick={() => setOpenCategory(isOpen ? null : row.category)}
                          className="rounded border border-slate-300 px-2 py-1 text-xs text-slate-600 hover:bg-slate-50"
                        >
                          {isOpen ? t.hideProducts : t.reviewProducts}
                        </button>
                      </td>
                    </tr>
                    {isOpen && (
                      <tr key={`${row.category}-products`}>
                        <td colSpan={4} className="bg-slate-50 py-3">
                          <ProductPicker
                            products={products}
                            loading={productsQuery.isPending}
                            pending={bulk.isPending}
                            onApply={(ids, shared) => bulk.mutate({ productIds: ids, shared })}
                          />
                        </td>
                      </tr>
                    )}
                  </>
                )
              })}
            </tbody>
          </table>
        </DataTableShell>
      )}
    </div>
  )
}

function ProductPicker({
  products,
  loading,
  pending,
  onApply,
}: {
  products: ProductCoverage[]
  loading: boolean
  pending: boolean
  onApply: (productIds: string[], shared: boolean) => void
}) {
  const t = useT(partnerApiI18n).catalog
  const [selected, setSelected] = useState<Set<string>>(new Set())

  const unsharedIds = products.filter((p) => !p.shared).map((p) => p.product_id)
  const sharedIds = products.filter((p) => p.shared).map((p) => p.product_id)

  function toggle(id: string) {
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  if (loading) return <p className="text-xs text-slate-500">{t.loadingProducts}</p>

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          disabled={pending || unsharedIds.length === 0}
          onClick={() => onApply(unsharedIds, true)}
          className="rounded bg-indigo-600 px-2 py-1 text-xs text-white hover:bg-indigo-700 disabled:bg-slate-200 disabled:text-slate-500"
        >
          {t.openAllUnshared(unsharedIds.length)}
        </button>
        <button
          type="button"
          disabled={pending || selected.size === 0}
          onClick={() => onApply([...selected], true)}
          className="rounded border border-slate-300 px-2 py-1 text-xs text-slate-600 disabled:text-slate-300"
        >
          {t.openSelected(selected.size)}
        </button>
        <button
          type="button"
          disabled={pending || sharedIds.length === 0}
          onClick={() => onApply(sharedIds, false)}
          className="rounded border border-rose-200 px-2 py-1 text-xs text-rose-600 hover:bg-rose-50 disabled:border-slate-200 disabled:text-slate-300"
        >
          {t.closeAllShared(sharedIds.length)}
        </button>
      </div>

      <ul className="max-h-72 divide-y divide-slate-200 overflow-auto rounded border border-slate-200 bg-white">
        {products.map((product) => (
          <li key={product.product_id} className="flex items-center gap-2 px-3 py-1.5">
            <input
              type="checkbox"
              checked={selected.has(product.product_id)}
              onChange={() => toggle(product.product_id)}
              aria-label={product.title}
            />
            <span className="min-w-0 flex-1 truncate text-xs text-slate-700">
              {product.display_code ? (
                <span className="mr-2 font-mono text-[10px] text-slate-400">
                  {product.display_code}
                </span>
              ) : null}
              {product.title}
            </span>
            {product.shared ? (
              <span className="inline-flex items-center gap-1 text-[10px] text-emerald-700">
                <Check className="h-3 w-3" /> {t.sharedBadge}
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 text-[10px] text-slate-400">
                <Lock className="h-3 w-3" /> {t.closedBadge}
              </span>
            )}
          </li>
        ))}
      </ul>
    </div>
  )
}
